import { AlertCircle, Bot, HelpCircle, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { IAiReplySettings, TAiReplyMode, TAiReplyPlatform } from '@/@types/aiReply';

const TIMEZONE_OPTIONS = [
  { value: 'Europe/Nicosia', label: 'Cyprus (Europe/Nicosia)' },
  { value: 'Europe/Athens', label: 'Greece (Europe/Athens)' },
  { value: 'Europe/London', label: 'UK (Europe/London)' },
  { value: 'Europe/Berlin', label: 'Germany (Europe/Berlin)' },
  { value: 'Europe/Moscow', label: 'Russia (Europe/Moscow)' },
  { value: 'Asia/Dubai', label: 'UAE (Asia/Dubai)' },
  { value: 'Asia/Jerusalem', label: 'Israel (Asia/Jerusalem)' },
  { value: 'America/New_York', label: 'US East (America/New_York)' },
  { value: 'America/Los_Angeles', label: 'US West (America/Los_Angeles)' },
  { value: 'UTC', label: 'UTC' },
];

interface ISettingToggleProps {
  // LOCAL_TYPE_JUSTIFIED: internal helper type for the SettingToggle sub-component only
  id: string;
  label: string;
  tooltip: string;
  checked: boolean;
  disabled?: boolean;
  indented?: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function SettingToggle({
  id,
  label,
  tooltip,
  checked,
  disabled,
  indented,
  onCheckedChange,
}: ISettingToggleProps) {
  return (
    <div className={cn('flex items-center justify-between', indented && 'ml-6')}>
      <div className="flex items-center gap-1.5">
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={label}
      />
    </div>
  );
}

interface IAiReplyGlobalSettingsProps {
  // LOCAL_TYPE_JUSTIFIED: props interface for this component
  settings: IAiReplySettings | null | undefined;
  timezone: string;
  onUpdate: (updates: {
    enabled?: boolean;
    mode?: TAiReplyMode;
    enabledPlatforms?: TAiReplyPlatform[];
    telegramEnabled?: boolean;
    telegramGroupsEnabled?: boolean;
    telegramPersonalEnabled?: boolean;
    whatsappEnabled?: boolean;
    whatsappGroupsEnabled?: boolean;
    whatsappPersonalEnabled?: boolean;
    emailEnabled?: boolean;
  }) => Promise<void>;
  onTimezoneChange: (tz: string) => Promise<void>;
}

export function AiReplyGlobalSettings({
  settings,
  timezone,
  onUpdate,
  onTimezoneChange,
}: IAiReplyGlobalSettingsProps) {
  if (settings === undefined) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bot aria-hidden="true" className="h-4 w-4" />
            Global Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-6 w-full" />
        </CardContent>
      </Card>
    );
  }

  const enabled = settings?.enabled ?? false;
  const mode = settings?.mode ?? 'draft';

  // Granular platform settings with defaults
  const telegramEnabled = settings?.telegramEnabled ?? true;
  const telegramGroupsEnabled = settings?.telegramGroupsEnabled ?? false;
  const telegramPersonalEnabled = settings?.telegramPersonalEnabled ?? true;
  const whatsappEnabled = settings?.whatsappEnabled ?? true;
  const whatsappGroupsEnabled = settings?.whatsappGroupsEnabled ?? false;
  const whatsappPersonalEnabled = settings?.whatsappPersonalEnabled ?? true;
  const emailEnabled = settings?.emailEnabled ?? true;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bot aria-hidden="true" className="h-4 w-4" />
            Global Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <SettingToggle
            id="ai-reply-enabled"
            label="Enable AI Auto-Reply"
            tooltip="Master switch — when off, AI won't process any messages on any platform"
            checked={enabled}
            onCheckedChange={checked => void onUpdate({ enabled: checked })}
          />

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="ai-reply-mode" className="text-sm font-medium">
                Reply Mode
              </Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>Draft saves replies for your review before sending. Auto sends immediately</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Select
              value={mode}
              onValueChange={value => void onUpdate({ mode: value as TAiReplyMode })} // AS_JUSTIFIED: Shadcn Select returns string, constrained by SelectItem values
              disabled={!enabled}
            >
              <SelectTrigger id="ai-reply-mode" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft — review before sending</SelectItem>
                <SelectItem value="auto">Auto — send immediately</SelectItem>
                <SelectItem value="voice_call">AI Voice Call — queue outbound call</SelectItem>
              </SelectContent>
            </Select>
            {mode === 'voice_call' && (
              <div className="flex items-start gap-2 rounded-md border border-yellow-500/50 bg-yellow-500/10 p-3">
                <AlertCircle
                  aria-hidden="true"
                  className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600 dark:text-yellow-400"
                />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Voice integration not configured
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    Calls will be queued in the database but no provider (ElevenLabs / Vapi) is
                    configured yet. Switch to &ldquo;Draft&rdquo; or &ldquo;Auto-send&rdquo; to
                    actually reach leads.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="ai-timezone" className="text-sm font-medium">
                Business Timezone
              </Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>Used for scheduling follow-up messages at appropriate local times</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Select value={timezone} onValueChange={value => void onTimezoneChange(value)}>
              <SelectTrigger id="ai-timezone" className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map(tz => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare aria-hidden="true" className="h-4 w-4" />
            Platform Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Telegram Section */}
          <div className="space-y-3">
            <SettingToggle
              id="telegram-enabled"
              label="Enable AI for Telegram"
              tooltip="Process incoming Telegram messages with AI classification and replies"
              checked={telegramEnabled}
              disabled={!enabled}
              onCheckedChange={checked => void onUpdate({ telegramEnabled: checked })}
            />
            <SettingToggle
              id="telegram-personal"
              label="Personal chats"
              tooltip="AI will analyze and reply to direct messages on Telegram"
              checked={telegramPersonalEnabled}
              disabled={!enabled || !telegramEnabled}
              indented
              onCheckedChange={checked => void onUpdate({ telegramPersonalEnabled: checked })}
            />
            <SettingToggle
              id="telegram-groups"
              label="Group chats"
              tooltip="AI will analyze and reply to messages in Telegram group conversations"
              checked={telegramGroupsEnabled}
              disabled={!enabled || !telegramEnabled}
              indented
              onCheckedChange={checked => void onUpdate({ telegramGroupsEnabled: checked })}
            />
          </div>

          <div className="border-t border-border" />

          {/* WhatsApp Section */}
          <div className="space-y-3">
            <SettingToggle
              id="whatsapp-enabled"
              label="Enable AI for WhatsApp"
              tooltip="Process incoming WhatsApp messages with AI classification and replies"
              checked={whatsappEnabled}
              disabled={!enabled}
              onCheckedChange={checked => void onUpdate({ whatsappEnabled: checked })}
            />
            <SettingToggle
              id="whatsapp-personal"
              label="Personal chats"
              tooltip="AI will analyze and reply to direct messages on WhatsApp"
              checked={whatsappPersonalEnabled}
              disabled={!enabled || !whatsappEnabled}
              indented
              onCheckedChange={checked => void onUpdate({ whatsappPersonalEnabled: checked })}
            />
            <SettingToggle
              id="whatsapp-groups"
              label="Group chats"
              tooltip="AI will analyze and reply to messages in WhatsApp group conversations"
              checked={whatsappGroupsEnabled}
              disabled={!enabled || !whatsappEnabled}
              indented
              onCheckedChange={checked => void onUpdate({ whatsappGroupsEnabled: checked })}
            />
          </div>

          <div className="border-t border-border" />

          {/* Email Section */}
          <div className="space-y-3">
            <SettingToggle
              id="email-enabled"
              label="Enable AI for Email"
              tooltip="AI will classify incoming emails and generate reply drafts"
              checked={emailEnabled}
              disabled={!enabled}
              onCheckedChange={checked => void onUpdate({ emailEnabled: checked })}
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
}
